import { useEffect, useMemo, useRef, useState } from "react";
import { START_PAGE } from "./nav";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import config from "./config";
import applyCommunityFont, { applyCommunityDirection, applyCommunityTheme } from "./applyFont";
import { applyThemeMode, paintedMode, resolveThemeMode } from "./theme";
import { paletteForMode } from "./brandColor";
import PageLoader from "./PageLoader";
import { setFavicon, DEFAULT_FAVICON } from "./favicon";
import { injectCustomScript } from "./injectCustomScript";
import { brandBackgroundStyle } from "./lib/brandBackground";
import { isPlatformDefaultLogo, BRAND_DEFAULT_SVG } from "./brandDefaultLogo";
import BrandDefaultMark from "./BrandDefaultMark";

/**
 * Dedicated membership login page — `/membership/:token/login`.
 *
 * A single, full-screen, brand-themed login (mirrors the legacy FlexiFunnels
 * membership login template). Three sub-views in one card:
 *   login   — email + password
 *   devices — shown when the login hits the device/session limit (api/login →
 *             { device_exist, device_data }). Lists the logged-in devices with a
 *             "Log out" CTA each; logging one out returns to the login form so
 *             the member can sign in again.
 *   forgot  — 3-step code reset: email → 6-digit code → new password
 *             (api/membership-send-otp / -verify-otp / -reset-password).
 *
 * Theming comes from the membership project's brand settings returned by
 * api/getmembership (funnel_data[0].brand_settings): logo, bgimg/bgmode/bgcolor,
 * theme_color1, button text
 * colour, overlay, rtl.
 */
const memberKey = (token) => `cm_${token}`;

export const DEFAULT_BRAND = {
  // Left as the legacy platform URL on purpose: it is what the API itself
  // substitutes for a membership with no logo, and brandDefaultLogo.js
  // recognises it and swaps in the themed placeholder. Changing it here would
  // only fix the case where the API sent nothing, which it never does.
  logo: "https://membershipdata.flexifunnels.com/images/logo.png",
  bgimg: "https://membershipdata.flexifunnels.com/images/login_bg_default.png",
  bgcolor: "#012329",
  bgmode: "image",
  bgtype: "cover",
  bgcolortype: "color",
  bggradientColor: "",
  theme_color1: "#012329",
  theme_color2: "#03B0D0",
  theme_button_txt_color: "#ffffff",
  overlaycolor: "rgba(0,0,0,0.5)",
  enable_overlay: 1,
  rtl_enabled: 0,
};

// Map the native api/login MembershipUser → the member object the app persists.
function toMember(data, funnelId, email) {
  const mu = data.MembershipUser || {};
  const token = data.access_token;
  return {
    community_member_id: mu.id,
    funnel_id: funnelId,
    name: mu.name || "",
    email: mu.email || email,
    img: mu.profile_img || null,
    points: mu.tot_points || 0,
    url_token: String(mu.id),
    auth_token: token,
    member_token: token,
  };
}

export default function MembershipLogin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [funnelPageData, setFunnelPageData] = useState(null);
  // True only while a ?code= sign-in is in flight, so the form is not flashed
  // at a member who is about to be redirected.
  const [autoLoggingIn, setAutoLoggingIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState("");
  /*
   * The creator's support button (admin → Settings → Features → Login Support
   * Button), which rides along with getmembership's feature flags — this screen
   * renders before sign-in, so there is no other call to ask for it on.
   */
  const [support, setSupport] = useState(null);

  // login | devices | forgot
  const [view, setView] = useState("login");
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  // Keyed by field, so each message renders under its own input instead of the
  // browser's native "Please fill in this field." bubble.
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPw, setShowPw] = useState(false);
  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((e) => (e[k] ? { ...e, [k]: "" } : e));
  };

  // Device-limit state
  const [devices, setDevices] = useState([]);
  const [deviceInfo, setDeviceInfo] = useState({ headline: "", message: "" });
  const [busyDeviceId, setBusyDeviceId] = useState(null);

  // Forgot-password is a 3-step CODE flow: 1 email → 2 six-digit code → 3 new
  // password. `otpToken` is the opaque token the API issues in step 1 and
  // requires on both later steps.
  const [forgotStep, setForgotStep] = useState(1);
  const [otpToken, setOtpToken] = useState("");
  const [otp, setOtp] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [showNewPw, setShowNewPw] = useState(false);

  const brand = useMemo(() => ({ ...DEFAULT_BRAND, ...(funnelPageData?.brand || {}) }), [funnelPageData]);
  // Brand → Logo & Banner → "Login page logo" wins here when the creator set
  // one; otherwise the login screen shows the ordinary brand logo, as before.
  const loginLogo = (typeof brand.login_logo === "string" && brand.login_logo.trim()) || brand.logo;
  // The card is bg-surface and the brand variable is published by then, so the
  // inline mark picks up both. isDefault decides which of the two to render.
  const logoIsDefault = isPlatformDefaultLogo(loginLogo);

  const destination = () => {
    // START_PAGE when nothing asked for a page: MembershipHome forwards it on
    // to the membership's Default page (Settings → Membership Menus).
    const next = searchParams.get("next") || searchParams.get("page") || START_PAGE;
    const ref = searchParams.get("ref");
    return `/membership/${token}/${next}${ref ? `?ref=${ref}` : ""}`;
  };

  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    (async () => {
      try {
        // Arrived via session-expiry: drop any stale member, stay on the form.
        const expired = searchParams.get("expired") === "1";
        if (expired) {
          try {
            localStorage.removeItem(memberKey(token));
          } catch {
            /* ignore */
          }
        }
        const existing = expired ? null : JSON.parse(localStorage.getItem(memberKey(token)) || "null");
        // Resolve the membership straight from the funnel (native api/getfunnel) —
        // it returns the numeric funnel_id + brand_settings for this membership.
        // The :token is the funnel's membership_code / funnel_code.
        const res = await axios.post(`${config.API_URL}/getmembership`, {
          membership_code: token,
          path: "login",
          // membership_code is unique per DOMAIN, not globally — two customers
          // can both own "team". Without the host the API resolves to whichever
          // funnel has the lower id, and every login on the other one fails with
          // "Invalid email or password". Same key the reset-password call below
          // already sends.
          base_url: window.location.host,
        });
        const fd = res.data?.funnel_data?.[0];
        if (!res.data?.success || !fd) {
          setFatal(res.data?.message || "Membership not found.");
        } else {
          let brand = {};
          try {
            brand = fd.brand_settings
              ? (typeof fd.brand_settings === "string" ? JSON.parse(fd.brand_settings) : fd.brand_settings)
              : {};
          } catch {
            brand = {};
          }
          const resolved = {
            funnel_id: fd.funnel_id,
            name: fd.name,
            brand,
            membership_font: brand.membership_font,
          };
          setFunnelPageData(resolved);
          /*
           * Prime the feature-flag cache that useBrand reads.
           *
           * Without this the app only learns the flags AFTER sign-in, from
           * getfunnel — so the lapsed-subscription gate had to wait for a second
           * round trip (login -> getfunnel -> get-myplans) and appeared about a
           * second late, after the member had already seen the content it is
           * meant to cover. Written under the same key useBrand seeds from, so
           * the shell knows the answer the moment it mounts.
           */
          try {
            localStorage.setItem(
              `features:${token}`,
              JSON.stringify(res.data?.features || {}),
            );
          } catch {
            /* ignore */
          }
          const feat = res.data?.features || {};
          // The API only flags this on when there is a link to open, but a stale
          // cached bundle could still be trusting the flag alone — check the URL
          // here too rather than render a button that goes nowhere.
          if (feat.login_support && feat.login_support_url) {
            setSupport({ text: feat.login_support_text || "", url: feat.login_support_url });
          }
          if (resolved.membership_font) applyCommunityFont(resolved.membership_font);
          applyCommunityDirection(brand?.rtl_enabled);
          applyCommunityTheme(brand);
          // Light/dark, same source as the rest of the app. Without this the
          // sign-in screen of a dark membership was the one light page in it.
          applyThemeMode(resolveThemeMode(brand, token), token);
          document.title = res.data.page_title || (resolved.name ? `Login · ${resolved.name}` : "Login");
          // Tab icon: the project's own favicon, else the bundled default. The
          // API already returns favicon_url here (with a platform fallback); it
          // was simply being discarded, so the login tab kept the CRA default.
          setFavicon(fd.favicon_url || DEFAULT_FAVICON);
          // Retargeting / analytics pixels (admin → Settings → General → General
          // Code) apply to the whole project, so the login page carries them too
          // — legacy bakes the same scripts into the member login page. Keyed by
          // the numeric project id; self-guarded and never throws.
          injectCustomScript(fd.funnel_id);
          if (existing?.member_token) {
            navigate(destination(), { replace: true });
            return;
          }

          // Auto-login via ?code= — the legacy Course_Membership behaviour
          // (src/pages/auth/[auth].js), which read the param on mount and
          // POSTed it to `autologin`. That endpoint still exists on the API
          // (AuthController@autoLogin) and decrypts the code into
          // "{email}-{funnel_id}"; only the frontend half was never ported, so
          // the parameter was silently ignored and the member just saw the form.
          //
          // Runs AFTER the membership resolves, because the session object needs
          // the numeric funnel_id, and only when there is no existing session —
          // an already-signed-in member is redirected above.
          const autoCode = searchParams.get("code");
          if (autoCode) {
            setAutoLoggingIn(true);
            try {
              const auto = await axios.post(`${config.API_URL}/autologin`, { code: autoCode });
              const ad = auto.data || {};

              // Device/session limit — same branch the manual login handles.
              if (ad.device_exist && Array.isArray(ad.device_data)) {
                setDevices(ad.device_data);
                setDeviceInfo({
                  headline: ad.message_headline || "Login session exceeded",
                  message: ad.message || "You've hit the maximum login sessions. Log out a device to continue.",
                });
                setView("devices");
                return;
              }

              if (ad.access_token && ad.MembershipUser?.id != null) {
                const m = toMember(ad, resolved.funnel_id, ad.MembershipUser?.email || "");
                localStorage.setItem(memberKey(token), JSON.stringify(m));
                navigate(destination(), { replace: true });
                return;
              }

              // A bad or expired code must not look like a crash: fall through
              // to the normal form so the member can simply sign in.
              setMsg({
                type: "error",
                text: ad.message || "That sign-in link is no longer valid. Please log in.",
              });
            } catch (e) {
              // Same reasoning — the link failed, the form still works.
              setMsg({
                type: "error",
                text: e.response?.data?.message || "That sign-in link is no longer valid. Please log in.",
              });
            } finally {
              setAutoLoggingIn(false);
            }
          }
        }
      } catch (e) {
        setFatal("Couldn't reach the server. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const finishLogin = (data) => {
    const m = toMember(data, funnelPageData?.funnel_id, form.email);
    localStorage.setItem(memberKey(token), JSON.stringify(m));
    toast.success(`Welcome back, ${m?.name || ""}!`.trim());
    navigate(destination(), { replace: true });
  };

  /**
   * A rejected sign-in reads under the password box, not in the banner above the
   * form. "Invalid email or password." is about the credential pair, and the
   * password is the field the member will retype, so it carries the message.
   * The banner stays for things that belong to no field at all (session
   * expired, device limits, the forgot-password steps).
   */
  const showLoginFailure = (serverMessage) => {
    // Banner only. The pair is wrong, not one specific box, so both inputs take
    // the border and the single message sits above them.
    setMsg({ type: "error", text: serverMessage || "Invalid email or password." });
    setFieldErrors({ email: true, password: true });
  };

  const submitLogin = async (e) => {
    e.preventDefault();

    // Collect both problems in one pass. The inputs used to rely on the `required`
    // attribute, so the browser blocked submit with its own native bubble and
    // neither the field nor the banner above could say anything.
    const email = form.email.trim();
    const next = {};
    if (!email) {
      next.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      next.email = "Enter a valid email address.";
    }
    if (!form.password) next.password = "Password is required.";

    const problems = Object.values(next).filter(Boolean);
    if (problems.length) {
      // Every message on this screen reports in the banner above the fields.
      // The fields themselves only take a red border, so it stays obvious which
      // one is at fault without repeating the text twice.
      setFieldErrors(next);
      setMsg({ type: "error", text: problems.length === 1 ? problems[0] : problems });

      return;
    }
    setFieldErrors({});
    setBusy(true);
    setMsg({ type: "", text: "" });
    try {
      const res = await axios.post(`${config.API_URL}/login`, {
        funnel_id: funnelPageData?.funnel_id,
        email: form.email,
        password: form.password,
        device_name: navigator.userAgent?.slice(0, 191) || "Web",
      });
      const data = res.data || {};
      // Device/session limit reached → show the device-management view.
      if (data.device_exist && Array.isArray(data.device_data)) {
        setDevices(data.device_data);
        setDeviceInfo({
          headline: data.message_headline || "Login session exceeded",
          message: data.message || "You've hit the maximum login sessions. Log out a device to continue.",
        });
        setView("devices");
        return;
      }
      // Blocked account or any other non-token response → surface the message.
      if (!data.access_token || data.MembershipUser?.id == null) {
        showLoginFailure(data.message);
        return;
      }
      finishLogin(data);
    } catch (err) {
      const m = err.response?.data?.message;
      showLoginFailure(m && m !== "Unauthorized" ? m : undefined);
    } finally {
      setBusy(false);
    }
  };

  // Log a specific device out, then return to the login form so the member can
  // sign in (they're now under the device limit).
  const logoutDevice = async (device) => {
    setBusyDeviceId(device.membership_device_id);
    try {
      await axios.post(
        `${config.API_URL}/logout`,
        { token: device.token, membership_device_id: device.membership_device_id },
        { headers: { Authorization: `Bearer ${device.token}` } }
      );
      const remaining = devices.filter((d) => d.membership_device_id !== device.membership_device_id);
      setDevices(remaining);
      toast.success("That device has been logged out.");
      // Redirect to THIS membership's own login page (not a generic view swap) so
      // the member lands on a fresh login form for the project they came from.
      // Preserve where they were headed (next/page) and any referral code.
      const params = new URLSearchParams();
      const next = searchParams.get("next") || searchParams.get("page");
      const ref = searchParams.get("ref");
      if (next) params.set("next", next);
      if (ref) params.set("ref", ref);
      const qs = params.toString();
      setForm((f) => ({ ...f, password: "" }));
      setView("login");
      setMsg({ type: "success", text: "Device logged out — please sign in again." });
      navigate(`/membership/${token}/login${qs ? `?${qs}` : ""}`, { replace: true });
    } catch (err) {
      toast.error("Couldn't log out that device. Please try again.");
    } finally {
      setBusyDeviceId(null);
    }
  };

  // Return the card to a clean login state (also used by every "Back to login").
  const resetForgot = () => {
    setView("login");
    setForgotStep(1);
    setOtpToken("");
    setOtp("");
    setNewPw("");
    setNewPw2("");
    setMsg({ type: "", text: "" });
  };

  /**
   * Forgot-password, 6-digit CODE flow (mirrors the creator-side reset):
   *   1 → api/membership-send-otp       email a code, returns an opaque token
   *   2 → api/membership-verify-otp     confirm the code
   *   3 → api/membership-reset-password set the new password
   *
   * The API answers 200 with {success:false,message} for expected problems
   * (wrong/expired code, weak password), so the reply is checked rather than
   * relying on the HTTP status.
   */
  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg({ type: "", text: "" });

    const funnel_id = funnelPageData?.funnel_id;
    try {
      if (forgotStep === 1) {
        const res = await axios.post(`${config.API_URL}/membership-send-otp`, {
          email: form.email,
          funnel_id,
        });
        if (!res.data?.success) {
          setMsg({ type: "error", text: res.data?.message || "Couldn't send the code." });
          return;
        }
        setOtpToken(res.data.token);
        toast.success(res.data.message);
        setForgotStep(2);
      } else if (forgotStep === 2) {
        const res = await axios.post(`${config.API_URL}/membership-verify-otp`, {
          email: form.email,
          funnel_id,
          otp,
          token: otpToken,
        });
        if (!res.data?.success) {
          setMsg({ type: "error", text: res.data?.message || "That code is not valid." });
          return;
        }
        setForgotStep(3);
      } else {
        const res = await axios.post(`${config.API_URL}/membership-reset-password`, {
          email: form.email,
          funnel_id,
          otp,
          token: otpToken,
          password: newPw,
          password_confirmation: newPw2,
        });
        if (!res.data?.success) {
          setMsg({ type: "error", text: res.data?.message || "Couldn't reset your password." });
          return;
        }
        toast.success(res.data.message || "Your password has been reset.");
        resetForgot();
      }
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.message || "Something went wrong. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  // Mode-corrected: the stock palette's #012329 is invisible on the dark
  // canvas, and this page renders in dark mode too (see `dark:bg-canvas`).
  const loginPalette = paletteForMode(brand, paintedMode());
  const theme = loginPalette.primary;
  const btnTxt = loginPalette.btnText;

  const bgStyle = brandBackgroundStyle(brand);

  // autoLoggingIn keeps the spinner up for the ?code= exchange. `loading` alone
  // happens to cover it today (the exchange runs inside the same effect, before
  // setLoading(false)), but relying on that coupling would break the moment the
  // effect is split — and the failure would be a login form flashed at a member
  // who is about to be redirected.
  if (loading || autoLoggingIn) {
    return (
      <div className="min-h-screen bg-[#f3f3f3] dark:bg-canvas">
        <PageLoader primary={theme} logo={logoIsDefault ? BRAND_DEFAULT_SVG : loginLogo} />
      </div>
    );
  }
  if (fatal) {
    return <div className="min-h-screen flex items-center justify-center bg-[#f3f3f3] dark:bg-canvas px-4 text-center text-red-500">{fatal}</div>;
  }

  const heading =
    view === "devices" ? (
      <>{deviceInfo.headline || "Login session exceeded"}</>
    ) : view === "forgot" ? (
      <>Reset <br /> your password</>
    ) : (
      <>Login with <br /> your account now</>
    );

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 relative" style={bgStyle}>
      {brand.bgmode !== "color" && Number(brand.enable_overlay) === 1 && (
        <div className="absolute inset-0" style={{ background: brand.overlaycolor || "rgba(0,0,0,0.5)" }} aria-hidden="true" />
      )}

      {/* The login card is fixed, non-localized English chrome and its layout
          (FloatingField icon/label sides, padding) is designed LTR. Isolate it
          as dir="ltr" so an RTL project (rtl_enabled=1) doesn't mirror the
          card and orphan punctuation ("Forgot Password?" → "?Forgot Password").
          The project's RTL still governs the content pages (feed/groups). */}
      {/* The card and the support button share a column: the button belongs to
          the page, not to the form, and sits under the card the way the login
          screens members already know put it. */}
      <div dir="ltr" className="relative flex w-full max-w-[380px] flex-col items-center gap-4">
        <div className="w-full bg-surface rounded-lg shadow-2xl px-7 py-9">
          <div className="flex flex-col items-center gap-5">
            {/* The placeholder is set smaller than the 60px a real logo gets: it
                carries a wordmark as well as a mark, so at the same height it
                dominates the card. */}
            {logoIsDefault ? (
              <BrandDefaultMark community={{ name: funnelPageData?.name }} className="h-[42px] w-auto" />
            ) : (
              <img src={loginLogo} alt={funnelPageData?.name || "Logo"} className="h-[60px] w-auto object-contain" />
            )}

            <p className="text-lg font-semibold text-black dark:text-gray-900 text-center leading-snug">{heading}</p>

            {msg.type === "error" && (Array.isArray(msg.text) ? msg.text.length > 0 : !!msg.text) && (
              <div className="w-full text-sm rounded-md px-3 py-2 bg-red-50 dark:bg-red-500/15 text-red-600 dark:text-red-300 border border-red-100 dark:border-red-500/30">
                {/* An array is several separate problems — one per line, rather
                    than run together into a single sentence. */}
                {Array.isArray(msg.text) ? (
                  <ul className="list-disc pl-4 space-y-0.5">
                    {msg.text.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                ) : (
                  msg.text
                )}
              </div>
            )}
            {msg.type === "success" && msg.text && (
              <div className="w-full text-sm rounded-md px-3 py-2 bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300 border border-green-100 dark:border-green-500/30">{msg.text}</div>
            )}
            {view === "login" && searchParams.get("expired") === "1" && !msg.text && (
              <div className="w-full text-sm rounded-md px-3 py-2 bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-100 dark:border-amber-500/30">
                Your session expired. Please log in again.
              </div>
            )}

            {/* ───── DEVICE LIMIT ───── */}
            {view === "devices" ? (
              <div className="w-full flex flex-col gap-3">
                <p className="text-sm text-gray-500 text-center -mt-2">
                  {deviceInfo.message || "You've hit the maximum login sessions. Log out a device to continue."}
                </p>
                <ul className="w-full flex flex-col gap-2">
                  {devices.length === 0 && (
                    <li className="text-sm text-gray-500 text-center py-3">All other devices are logged out — please sign in.</li>
                  )}
                  {devices.map((d) => (
                    <li
                      key={d.membership_device_id}
                      className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2.5"
                    >
                      <span className="shrink-0 text-gray-400">{ICON.device}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-800 truncate">{deviceLabel(d)}</p>
                        <p className="text-xs text-gray-400">Active session</p>
                      </div>
                      <button
                        type="button"
                        disabled={busyDeviceId === d.membership_device_id}
                        onClick={() => logoutDevice(d)}
                        className="shrink-0 rounded-md px-3 py-1.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
                        style={{ backgroundColor: theme }}
                      >
                        {busyDeviceId === d.membership_device_id ? "…" : "Log out"}
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => { setView("login"); setMsg({ type: "", text: "" }); }}
                  className="text-center text-sm font-medium mt-1"
                  style={{ color: theme }}
                >
                  ← Back to login
                </button>
              </div>
            ) : (
              /* ───── LOGIN / FORGOT form ───── */
              <form className="w-full flex flex-col gap-4" onSubmit={view === "forgot" ? submitForgot : submitLogin}>
                {/* Step 1 (and login) ask for the email; later forgot steps keep it fixed. */}
                {(view === "login" || forgotStep === 1) && (
                  <FloatingField label="Enter Your Email" type="email" value={form.email} onChange={set("email")} theme={theme} disabled={busy} icon={ICON.mail} invalid={!!fieldErrors.email} />
                )}

                {view === "forgot" && forgotStep === 2 && (
                  <>
                    <p className="text-sm text-gray-500 text-center">
                      We've sent a 6-digit code to <span className="font-medium text-gray-700">{form.email}</span>. It expires in 15 minutes.
                    </p>
                    <FloatingField
                      label="6-Digit Code"
                      type="text"
                      value={otp}
                      /* digits only, so a pasted code with spaces still works */
                      onChange={(v) => setOtp(String(v).replace(/\D/g, "").slice(0, 6))}
                      theme={theme}
                      disabled={busy}
                      required
                    />
                  </>
                )}

                {view === "forgot" && forgotStep === 3 && (
                  <>
                    <FloatingField
                      label="New Password"
                      type={showNewPw ? "text" : "password"}
                      value={newPw}
                      onChange={setNewPw}
                      theme={theme}
                      disabled={busy}
                      required
                      icon={showNewPw ? ICON.eyeOff : ICON.eye}
                      onIconClick={() => setShowNewPw((v) => !v)}
                    />
                    <FloatingField
                      label="Confirm New Password"
                      type={showNewPw ? "text" : "password"}
                      value={newPw2}
                      onChange={setNewPw2}
                      theme={theme}
                      disabled={busy}
                      required
                    />
                  </>
                )}

                {view === "login" && (
                  <>
                    <FloatingField
                      label="Enter Your Password"
                      type={showPw ? "text" : "password"}
                      value={form.password}
                      onChange={set("password")}
                      theme={theme}
                      disabled={busy}
                      invalid={!!fieldErrors.password}
                      icon={showPw ? ICON.eyeOff : ICON.eye}
                      onIconClick={() => setShowPw((s) => !s)}
                    />
                    {/* Right edge of the password field it belongs to, in small
                        type: a secondary way out, not a second CTA competing
                        with Login. */}
                    <button
                      type="button"
                      onClick={() => { setView("forgot"); setMsg({ type: "", text: "" }); }}
                      className="-mt-2 self-end text-[12.5px] font-semibold hover:underline"
                      style={{ color: theme }}
                    >
                      Forgot Password?
                    </button>
                  </>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  className="w-full text-center text-lg rounded font-medium py-2.5 transition hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: theme, color: btnTxt }}
                >
                  {busy
                    ? "Please wait…"
                    : view !== "forgot"
                      ? "Login"
                      : forgotStep === 1
                        ? "Send Code"
                        : forgotStep === 2
                          ? "Verify Code"
                          : "Reset Password"}
                </button>

                {view === "forgot" && (
                  <div className="flex flex-col gap-1">
                    {forgotStep === 2 && (
                      <button
                        type="button"
                        onClick={() => { setForgotStep(1); setOtp(""); setMsg({ type: "", text: "" }); }}
                        className="text-center text-sm font-medium text-gray-500 underline"
                      >
                        Use a different email
                      </button>
                    )}
                    <button type="button" onClick={resetForgot} className="text-center text-sm font-medium" style={{ color: theme }}>
                      ← Back to login
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>

        {/* Creator's own support link (Settings → Features → Login Support
            Button). Shown on every step of this screen — login, forgot password
            and the device limit — because being stuck is exactly when a member
            needs it. Only rendered when a link was actually saved. */}
        {support ? (
          <a
            href={support.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex max-w-full items-center gap-2 rounded-full border px-5 py-2.5 text-center text-[13.5px] font-semibold leading-snug shadow-lg backdrop-blur-sm transition-all duration-200 hover:-translate-y-px hover:shadow-xl"
            style={{ borderColor: `${theme}4d`, color: theme, backgroundColor: "rgb(var(--c-surface) / 0.92)" }}
          >
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <circle cx="12" cy="12" r="3.6" />
              <path strokeLinecap="round" d="m5.7 5.7 3.8 3.8m5 5 3.8 3.8m0-12.6-3.8 3.8m-5 5-3.8 3.8" />
            </svg>
            <span className="min-w-0">{support.text || "Still stuck? Contact support"}</span>
            <svg className="h-3.5 w-3.5 shrink-0 opacity-60 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:opacity-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </a>
        ) : null}
      </div>
    </div>
  );
}

function deviceLabel(d) {
  const name = String(d.device_name || "").trim();
  if (name) {
    // device_name is often the raw user-agent — shorten to something readable.
    const ua = name.toLowerCase();
    const os = /windows/.test(ua) ? "Windows" : /mac/.test(ua) ? "Mac" : /android/.test(ua) ? "Android" : /iphone|ipad|ios/.test(ua) ? "iOS" : /linux/.test(ua) ? "Linux" : "";
    const br = /edg/.test(ua) ? "Edge" : /chrome/.test(ua) ? "Chrome" : /firefox/.test(ua) ? "Firefox" : /safari/.test(ua) ? "Safari" : "";
    if (os || br) return [br, os].filter(Boolean).join(" · ");
    return name.length > 40 ? `${name.slice(0, 40)}…` : name;
  }
  return `Device #${d.membership_device_id}`;
}

/**
 * `error`   — red border AND the message rendered under the input.
 * `invalid` — red border only; used by the login form, which reports every
 *             message in the banner above the fields instead.
 */
export function FloatingField({ label, type = "text", value, onChange, theme, disabled, required, icon, onIconClick, error, invalid }) {
  const showRing = !!error || !!invalid;
  const [focused, setFocused] = useState(false);
  const [autofilled, setAutofilled] = useState(false);
  const inputRef = useRef(null);

  // Browsers can autofill the DOM input (saved credentials) without firing onChange,
  // leaving React state empty. Detect it on mount so the label floats correctly and
  // the autofilled value gets synced into state. Autofill can land asynchronously,
  // so re-check over a short window.
  useEffect(() => {
    const sync = () => {
      const el = inputRef.current;
      if (!el) return;
      let isAutofilled = false;
      try {
        isAutofilled = el.matches(":-webkit-autofill") || el.matches(":autofill");
      } catch {
        // pseudo-class unsupported in this browser; fall back to value check
      }
      if (el.value) {
        setAutofilled(isAutofilled || el.value !== value);
        if (el.value !== value) onChange(el.value);
      }
    };
    sync();
    const timers = [50, 150, 400].map((ms) => setTimeout(sync, ms));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const floated = focused || autofilled || (value !== undefined && value !== "");
  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type={type}
        value={value}
        required={required}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          if (autofilled) setAutofilled(false);
          onChange(e.target.value);
        }}
        onAnimationStart={(e) => {
          // Chrome/Safari fire these when (un)autofilling — see index.css.
          const name = (e.animationName || "").toLowerCase();
          if (name.includes("onautofillstart")) setAutofilled(true);
          else if (name.includes("onautofillcancel")) setAutofilled(false);
        }}
        autoComplete="off"
        aria-invalid={showRing ? "true" : undefined}
        className="peer w-full rounded-md border bg-surface pt-5 pb-2 pl-4 pr-11 text-[16px] text-gray-900 outline-none transition-colors"
        style={{
          borderColor: showRing ? "#dc2626" : focused ? theme : "rgb(var(--c-gray-300))",
          boxShadow: showRing
            ? "0 0 0 2px rgba(220,38,38,0.15)"
            : focused
            ? `0 0 0 2px ${theme}22`
            : "none",
        }}
      />
      <label
        className="pointer-events-none absolute left-4 transition-all duration-150"
        style={{
          color: floated ? theme : "rgb(var(--c-gray-400))",
          top: floated ? "7px" : "50%",
          transform: floated ? "translateY(0)" : "translateY(-50%)",
          fontSize: floated ? "11px" : "15px",
        }}
      >
        {label}
      </label>
      {icon && (
        <span
          onClick={onIconClick}
          className={`absolute right-3 top-1/2 -translate-y-1/2 ${onIconClick ? "cursor-pointer" : "pointer-events-none"}`}
          style={{ color: "rgb(var(--c-gray-600))" }}
        >
          {icon}
        </span>
      )}
      {error ? (
        <p className="mt-1.5 text-[12px] font-medium text-red-600 dark:text-red-300">{error}</p>
      ) : null}
    </div>
  );
}

export const ICON = {
  mail: (
    <svg width="22" height="18" viewBox="0 0 22 18" fill="currentColor">
      <path d="M3 0a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h16a3 3 0 0 0 3-3V3a3 3 0 0 0-3-3H3Zm0 2h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Z" fillRule="evenodd" />
      <path d="M11 8.78 1.57 2.18A1 1 0 0 1 2.72.54l9.42 6.6L21.57.54a1 1 0 1 1 1.15 1.64L11 8.78Z" transform="translate(-1 1.6)" />
    </svg>
  ),
  eye: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12S6 5 12 5s9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  eyeOff: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.6 10.6a3 3 0 0 0 4.2 4.2M9.9 5.2A9.5 9.5 0 0 1 12 5c6 0 9.5 7 9.5 7a16 16 0 0 1-3 3.6M6.1 6.1A16 16 0 0 0 2.5 12S6 19 12 19a9.2 9.2 0 0 0 3.9-.8" />
    </svg>
  ),
  device: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path strokeLinecap="round" d="M8 20h8M12 17v3" />
    </svg>
  ),
};
